<script lang="ts" setup>
import { defineRule, Field as VeeField, Form as VeeForm } from 'vee-validate'

import { requiredValidator } from '@/utils/rulesOfValidate'

import { useAppStore } from '@/store/app/app'
import { SnackbarType } from '@/store/app/app.types'
import { useBasketStore } from '@/store/basket/basket'
import { useCityStore } from '@/store/city/city'

import {
  HttpStatusCode,
  NotFoundError,
  ServerError,
  Success,
} from '@/api/results'
import { CompositionOfWok, Product } from '@/types/api/api-v3/make-wok'

import WokAlert from '@/components/Wok/WokAlert.vue'
import WokCategory from '@/components/Wok/WokCategory.vue'
import WokStatusPanel from '@/components/Wok/WokStatusPanel.vue'

const { isDesktop } = useResponsive()

const cityStore = useCityStore()
const basketStore = useBasketStore()

const { showSnackbar } = useAppStore()
const { $sentryCaptureException } = useNuxtApp()

const compositionOfWok = ref({} as CompositionOfWok)

const wokConstructorEnabled = computed(
  () => cityStore.cityInfo.wok_constructor_enabled,
)

const {
  cityInfo: { domain },
} = useCityStore()

const { data: page, error } = await useAsyncData(async (nuxtApp) => {
  if (!wokConstructorEnabled.value) {
    throw createError({
      statusCode: HttpStatusCode.NOT_FOUND,
    })
  }

  const result = await nuxtApp?.$apiService.wok.fetchPage()

  if (result instanceof NotFoundError) {
    throw createError({
      statusCode: HttpStatusCode.NOT_FOUND,
    })
  }

  if (result instanceof ServerError) {
    throw createError({
      statusCode: HttpStatusCode.INTERNAL_SERVER_ERROR,
    })
  }

  if (result instanceof Success) return result.data
})

if (error.value) showError(error.value)

const selectedWok = computed(() => {
  const compositionOfWokProductId = new Set(
    Object.keys(compositionOfWok.value).map(
      (category) => compositionOfWok.value[category].id,
    ),
  )

  return page.value?.products.find((wok) => {
    return wok.components.every((component) => {
      return compositionOfWokProductId.has(component.product_id)
    })
  })
})

watch(selectedWok, (newValue) => {
  if (!newValue) return

  isVisibleStaticSnackbar.value = false
})

const padeg = computed(() => cityStore.cityInfo.padeg)

const breadcrumbs = computed(() => [
  { text: `Доставка суши в ${padeg.value?.pred}`, href: '/' },
  { text: `Лапша WOK в ${padeg.value?.pred}`, href: '/category/wok/' },
  { text: `Конструктор WOK в ${padeg.value?.pred}`, href: '/make-wok/' },
])

const categories = computed(() => page.value?.categories || [])

const currencySymbol = computed(() => cityStore.currencySymbol)

const showBreadcrumb = computed(
  () => breadcrumbs.value.length !== 0 && isDesktop.value,
)

const totalWeight = computed(() => {
  let totalWeight = 0

  Object.keys(compositionOfWok.value).forEach((category) => {
    totalWeight = totalWeight + (compositionOfWok.value[category].weight || 0)
  })

  return totalWeight
})

const totalPrice = computed(() => {
  let totalPrice = 0

  Object.keys(compositionOfWok.value).forEach((category) => {
    totalPrice = totalPrice + (compositionOfWok.value[category].price || 0)
  })

  return totalPrice
})

const basketError = computed(() => basketStore.error)
const basketIsDisabled = computed(() => basketStore.basketIsDisabled)

const getProductOfCategory = (categoryName: string, productId: number) => {
  const category = page.value?.categories.find(
    (category) => category.name === categoryName,
  )

  if (!category) return undefined

  return category.products.find((product) => product.id === productId)
}

const setProductToCompositionOfWok = (
  categoryName: string,
  product: Product | undefined,
) => {
  // @ts-ignore
  compositionOfWok.value[categoryName] = product
}

const addIngredientInCompositionOfWok = (
  categoryName: string,
  productId: number,
) => {
  const product = getProductOfCategory(categoryName, productId)

  setProductToCompositionOfWok(categoryName, product)
}

const clearCompositionOfWok = () => (compositionOfWok.value = {})

const addIngredient = (categoryName: string, productId: number) => {
  const product: Product | undefined = getProductOfCategory(
    categoryName,
    productId,
  )

  if (!product) {
    showSnackbar({
      message: 'Что-то пошло не так',
    })

    return
  }

  addIngredientInCompositionOfWok(categoryName, productId)
}

const isVisibleStaticSnackbar = ref(false)

const showStaticSnackbar = () => {
  if (isDesktop.value) {
    isVisibleStaticSnackbar.value = true
    return
  }

  showSnackbar({
    message: 'Выберите, пожалуйста, ингредиенты',
  })
}

const addWokToBasket = async () => {
  if (!selectedWok.value) {
    isVisibleStaticSnackbar.value = true

    return
  }

  await basketStore.addProduct(selectedWok.value.id)

  if (!basketError.value) {
    clearCompositionOfWok()

    showSnackbar({
      message: 'Добавлено в корзину',
      type: SnackbarType.SUCCESS,
    })
  } else if (
    basketError.value.response?.status === 422 &&
    basketError.value.response?.data?.product_id
  ) {
    showSnackbar({
      message: basketError.value.response?.data?.product_id[0],
    })
  } else if (basketIsDisabled.value) {
    showSnackbar({
      message: 'Приём заказов приостановлен',
    })
  } else {
    showSnackbar({
      message: 'Что-то пошло не так :( Попробуйте позже',
    })

    // @ts-ignore
    $sentryCaptureException(basketError.value)
  }
}

defineRule('required', requiredValidator)
</script>

<template>
  <div class="page">
    <app-head
      :title="page?.seo?.seo_title"
      :seo="{
        title: page?.seo?.seo_title,
        description: page?.seo?.seo_description,
        keywords: page?.seo?.seo_keywords,
        seo_title: page?.seo?.seo_title,
      }"
    />

    <the-breadcrumbs
      v-if="showBreadcrumb"
      class="page__breadcrumbs"
      :breadcrumbs="breadcrumbs"
      :domain="domain"
    />

    <h1 class="page__title">Соберите свой WOK</h1>

    <vee-form class="page__inner">
      <div class="page__categories">
        <vee-field
          v-for="(category, index) in categories"
          :key="category.name"
          immediate
          :vid="category.name"
          :name="category.name"
          rules="required"
        >
          <wok-category
            :description="category.description"
            :name="category.name"
            :number-category="index"
            :products="category.products"
            :currency="currencySymbol"
            :value="
              compositionOfWok[category.name]
                ? compositionOfWok[category.name].id
                : null
            "
            @input="addIngredient"
          />
          <hr v-if="index !== categories.length - 1" class="page__divider" />
        </vee-field>
      </div>

      <div class="page__aside">
        <transition v-if="isDesktop" name="alert">
          <wok-alert
            v-if="!selectedWok && isVisibleStaticSnackbar"
            class="page__alert"
          >
            Выберите, пожалуйста, ингредиенты
          </wok-alert>
        </transition>

        <wok-status-panel
          :composition-of-wok="compositionOfWok"
          :categories="categories"
          :total-price="totalPrice"
          :total-weight="totalWeight"
          :currency="currencySymbol"
          :selected-wok="selectedWok"
          @add-wok-to-basket="addWokToBasket"
          @on-disabled-add="showStaticSnackbar"
        />
      </div>
    </vee-form>
  </div>
</template>
<style lang="scss" scoped>
.page {
  @include media('>=desktop-mini') {
    padding-top: 50px;
  }

  &__breadcrumbs {
    margin-bottom: 50px;
  }

  &__title {
    margin-bottom: 26px;
    font-size: 18px;
    font-weight: 800;
    color: var(--ui-text-primary);

    @include media('>=desktop-mini') {
      margin-bottom: 30px;
      font-size: 28px;
    }
  }

  &__inner {
    display: flex;
    flex-direction: column;

    @include media('>=desktop-mini') {
      flex-direction: row;
      justify-content: space-between;
    }
  }

  &__categories {
    flex: 1 1 auto;
    margin-bottom: 20px;

    @include media('>=desktop-mini') {
      margin-right: 85px;
      margin-bottom: 0;
    }
  }

  &__divider {
    margin: 40px 0 30px;
    border-color: var(--ui-grey);
    border-width: 0 0 1px 0;

    @include media('>=desktop-mini') {
      margin: 30px 0 20px;
    }
  }

  &__aside {
    position: sticky;
    bottom: 0;
    display: flex;
    flex-direction: column;
    margin: 0 -16px;

    @include media('>=desktop-mini') {
      top: 138px;
      bottom: unset;
      flex: 0 0 384px;
      align-self: flex-start;
      margin: 0;
    }
  }

  &__alert {
    margin: 0 10px 16px 10px;
    @include media('>=desktop-mini') {
      order: 2;
      margin: 60px auto 0 0;
    }
  }
}

.alert-enter-active,
.alert-leave-active {
  max-height: 400px;
  opacity: 0.9;
  transition: 0.15s ease-in-out;
}

.alert-enter,
.alert-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
